/* eslint-disable no-underscore-dangle,no-await-in-loop,class-methods-use-this */
import { EventTarget, defineEventAttribute } from 'event-target-shim';

const { promisify } = require('es6-promisify');

export default (bottle) => {
  bottle.factory('Gatekeeper', ({
    session,
    CALLBACK_ERROR,
    SESSION_START,
    SESSION_ERROR,
    SESSION_END,
    SESSION,
    sleep,
    now,
    window,
    jsonString,
    WebAuth,
    CustomEvent,
    clearHash,
  }) => {
    class Gatekeeper extends EventTarget {
      constructor(config = {}) {
        super();
        if (!config.domain) throw new Error(`Gatekeeper requires domain, ${jsonString(config)}`);
        if (!config.clientID) throw new Error(`Gatekeeper requires clientID, ${jsonString(config)}`);
        this.__auth = new WebAuth({
          responseType: 'token id_token',
          redirectUri: config.redirectUrl || `${window.location.origin}/callback`,
          ...config,
        });

        try {
          this.__auth.crossOriginVerification();
        } catch (err) {
          console.log('crossOriginVerification error:', err);
        }
      }

      // Public members
      get currentSession() {
        return session.get();
      }

      set currentSession(value) {
        session.set(value);
      }

      async start() {
        if (!this.__currentSessionStream) {
          this.__currentSessionStream = this.__startSessionStream()
            .finally(() => {
              this.__currentSessionStream = null;
            });
        }

        return this.__currentSessionStream;
      }

      async startFromCallback() {
        try {
          this.currentSession = await this.parseHash();
          if (this.currentSession) {
            this._dispatch(SESSION, { session: this.currentSession });
          }
          clearHash();
        } catch (error) {
          this._dispatch(CALLBACK_ERROR, { error });
        }
      }


      getProfile(...args) {
        const info = this.__auth.client.userInfo.bind(this.__auth.client);
        const promisedGetAuthClientUserInfo = promisify(info);
        return promisedGetAuthClientUserInfo(...args);
      }

      checkSession(config = {}) {
        const promisedAuthSession = promisify(this.__auth.checkSession.bind(this.__auth));
        return promisedAuthSession(config);
      }

      parseHash(config = {}) {
        // although auth0 will get hash from the location, in order to "close the environment"
        // we are manually injecting hash from our local window.location to enhance testibility
        const params = { ...config };
        if (window.location && window.location.hash) {
          params.hash = window.location.hash;
        }
        const parseAuthHash = promisify(this.__auth.parseHash.bind(this.__auth));
        return parseAuthHash(params);
      }

      authorize(config = {}) {
        this.__auth.authorize({ ...config });
      }

      terminate(config = {}) {
        this.currentSession = undefined;
        this.__auth.logout({ ...config });
      }

      // Private members

      _dispatch(type, detail = {}) {
        this.dispatchEvent(new CustomEvent(type, { detail }));
      }

      __currentSessionStream;

      async __startSessionStream() {
        this._dispatch(SESSION_START, { session: this.currentSession });

        try {
          const currentSession = this.__sessionStreamGen(this.currentSession);

          // eslint-disable-next-line no-restricted-syntax
          for await (const sessionState of currentSession) {
            this.currentSession = await sessionState;
            this._dispatch(SESSION, { session: this.currentSession });
          }
        } catch (error) {
          this._dispatch(SESSION_ERROR, { error });
        }

        this._dispatch(SESSION_END);
      }

      async* __sessionStreamGen(retrievedSession) {
        let alive = true;

        if (retrievedSession && retrievedSession.expiresIn - now() > 0) {
          yield retrievedSession;
          await sleep(retrievedSession.expiresIn - now());
        }

        while (alive) {
          try {
            const authResponse = await this.checkSession();
            const expiresIn = (authResponse.expiresIn * 1000) + now();

            const newSession = {
              ...authResponse,
              expiresIn,
            };

            yield newSession;

            await sleep(expiresIn - now());
          } catch (error) {
            alive = false;
            throw error;
          }
        }
      }
    }

    // Gatekeeper `Gatekeeper.onevent` properties.
    defineEventAttribute(Gatekeeper.prototype, SESSION);
    defineEventAttribute(Gatekeeper.prototype, SESSION_END);
    defineEventAttribute(Gatekeeper.prototype, SESSION_START);
    defineEventAttribute(Gatekeeper.prototype, SESSION_ERROR);
    defineEventAttribute(Gatekeeper.prototype, CALLBACK_ERROR);

    return Gatekeeper;
  });
};



// WEBPACK FOOTER //
// ./src/Gatekeeper.js